import type { EventsRole } from '../../../types/eventsRole';

export type { EventsRole };

/** Action names from stores/events_permissions.py::ACTION_ROLES. */
export type EventsCapability =
  | 'session.read'
  | 'ops.read'
  | 'crm.read'
  | 'commercial.read'
  | 'approval.read'
  | 'reconciliation.read'
  | 'lead.write'
  | 'lead.convert'
  | 'event.update'
  | 'booking.lose'
  | 'booking.cancel'
  | 'hold.place'
  | 'hold.release'
  | 'proposal.edit'
  | 'proposal.publish'
  | 'message.send'
  | 'note.write'
  | 'task.complete'
  | 'approval.resolve'
  | 'payment.request'
  | 'payment.refund'
  | 'settings.venue';

export type EventsVenue = {
  id: number;
  name: string;
  slug: string;
  /** IANA zone from Store.timezone; null when unset or invalid. */
  timezone?: string | null;
};

/** One row of GET /api/events/organizations/. */
export type EventsOrganization = {
  id: number;
  name: string;
  enabled: boolean;
  reconciliation_only: boolean;
  venues: EventsVenue[];
};

/** GET /api/events/session/. Server-authoritative; never derived locally. */
export type EventsSession = {
  organization_id: number;
  venue_id: number | null;
  role: EventsRole;
  is_approver: boolean;
  approval_limit_cents: number | null;
  enabled: boolean;
  reconciliation_only: boolean;
  capabilities: string[];
};

export type EventsScope = {
  organizationId: number;
  venueId: number | null;
};

export type BookingLifecycleState =
  | 'PROSPECT'
  | 'CONFIRMED'
  | 'CANCELLATION_PENDING'
  | 'CANCELLED';

export type BookingPaymentState =
  | 'NONE'
  | 'DEPOSIT_DUE'
  | 'PAID'
  | 'PAST_DUE'
  | 'REFUND_PENDING'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED';

/** events/serializers.py::ContactSerializer. */
export type EventsContact = {
  id: number;
  venue: number | null;
  name: string;
  email: string;
  phone: string;
  account: number | null;
  account_name?: string | null;
};

/** events/serializers.py::SpaceSerializer. */
export type EventsSpace = {
  id: number;
  venue: number;
  code: string;
  name: string;
  capacity_seated: number | null;
  capacity_standing: number | null;
  is_combinable: boolean;
  parent_space: number | null;
  active: boolean;
};

export type BookingSalesStage =
  | 'NEW'
  | 'QUALIFIED'
  | 'PROPOSAL_DRAFT'
  | 'PROPOSAL_SENT'
  | 'CONTRACT_DEPOSIT'
  | 'WON'
  | 'LOST';

export type ContractState = 'NONE' | 'PENDING' | 'SIGNED';

export type EventLifecycleState =
  | 'PROSPECT'
  | 'HELD'
  | 'CONFIRMATION_BLOCKED'
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

/** events/serializers.py::HoldSerializer. */
export type EventsHold = {
  id: number;
  space: number;
  kind: 'SOFT' | 'HARD';
  status: string;
  expires_at: string | null;
};

/** events/serializers.py::EventSerializer, the fields the UI reads. */
export type EventsEvent = {
  id: number;
  starts_at: string | null;
  ends_at: string | null;
  guest_count: number | null;
  guest_count_min?: number | null;
  guest_count_max?: number | null;
  event_type?: string;
  requires_space?: boolean;
  lifecycle_state?: EventLifecycleState;
  inventory_state?: 'NONE' | 'SOFT_HOLD' | 'HARD_HOLD';
  spaces?: Pick<EventsSpace, 'id' | 'name'>[];
  holds?: EventsHold[];
};

/** events/serializers.py::BookingSerializer. */
export type EventsBooking = {
  id: number;
  venue: number;
  lead?: number;
  primary_contact: Pick<EventsContact, 'id' | 'name'> & Partial<EventsContact>;
  owner?: number | null;
  sales_stage?: BookingSalesStage;
  lifecycle_state: BookingLifecycleState;
  contract_state?: ContractState;
  payment_state: BookingPaymentState;
  currency: string;
  event: EventsEvent | null;
  created_at?: string;
  updated_at: string;
};

export const PIPELINE_COLUMNS = [
  'NEW',
  'QUALIFIED',
  'PROPOSAL_SENT',
  'CONTRACT_DEPOSIT',
  'CONFIRMED',
  'LOST',
] as const;

export type PipelineColumn = (typeof PIPELINE_COLUMNS)[number];

/** GET pipeline/: bookings grouped by events/bookings/transitions.py::derive_column. */
export type EventsPipeline = Record<PipelineColumn, EventsBooking[]>;

export type LeadSource =
  | 'STAFF'
  | 'PUBLIC_FORM'
  | 'RETELL_EVENT'
  | 'RETELL_CATERING';

export type LeadStage = 'NEW' | 'QUALIFIED' | 'LOST';

/** events/serializers.py::LeadSerializer. */
export type EventsLead = {
  id: number;
  venue: number | null;
  requested_venue: number | null;
  primary_contact: EventsContact;
  thread_id: number | null;
  source: LeadSource;
  source_ref: string;
  requested_date: string | null;
  requested_start: string | null;
  requested_end: string | null;
  guest_count: number | null;
  budget_min_cents: number | null;
  budget_max_cents: number | null;
  event_type: string;
  requires_space: boolean;
  notes: string;
  qualification_score: number | null;
  sales_stage: LeadStage;
  lost_reason: string;
  owner: number | null;
  possible_duplicate_of: number | null;
  created_at: string;
  updated_at: string;
};

export type LeadDetails = Pick<
  EventsLead,
  | 'requested_date'
  | 'requested_start'
  | 'requested_end'
  | 'guest_count'
  | 'budget_min_cents'
  | 'budget_max_cents'
  | 'event_type'
  | 'requires_space'
  | 'notes'
>;

/**
 * events/serializers.py::LeadUpdateSerializer. On a lead without a venue,
 * `requested_venue_id` also assigns it to that venue, once, moving its thread
 * and messages; on an assigned lead any other venue (or null) is refused.
 */
export type LeadUpdate = Partial<LeadDetails> & {
  requested_venue_id?: number | null;
};

/** events/serializers.py::ConvertLeadSerializer. */
export type ConvertLeadInput = {
  starts_at: string;
  ends_at: string;
  guest_count?: number | null;
  event_type?: string;
  requires_space?: boolean;
  space_ids?: number[];
};

export type EventsBookingFilters = {
  state?: BookingLifecycleState;
};

export type PaymentRequestState =
  | 'PENDING'
  | 'REQUESTED'
  | 'PAID'
  | 'FAILED'
  | 'EXPIRED'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'WAIVED';

/** commercial/serializers.py::PaymentRequestSerializer. */
export type EventsPaymentRequest = {
  id: number;
  booking_id: number;
  kind: string;
  amount_cents: number;
  currency: string;
  due_at: string | null;
  state: PaymentRequestState;
  attempt: number;
  created_at: string;
  updated_at: string;
};

export type BookingRefundState =
  | 'REQUESTED'
  | 'SUBMITTED'
  | 'SUCCEEDED'
  | 'FAILED';

/** commercial/serializers.py::BookingRefundSerializer. */
export type EventsBookingRefund = {
  id: number;
  booking_id: number;
  payment_id: number;
  amount_cents: number;
  state: BookingRefundState;
  stripe_refund_id: string | null;
  reason: string;
  requested_by?: number | null;
  approved_by?: number | null;
  /** The failed refund this attempt retries; a failed refund retries once. */
  retry_of?: number | null;
  provider_succeeded_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ApprovalState = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

/**
 * workflow/serializers.py::ApprovalRequestSerializer. `action` is
 * proposal.publish, payment.refund or confirmation.resolve; the last is
 * resolved on the booking (retry or cancel and refund), never approved here.
 */
export type EventsApproval = {
  id: number;
  venue: number | null;
  policy: number | null;
  action: string;
  aggregate_type: string;
  aggregate_id: string;
  booking_id: number | null;
  /** Set on monetary approvals; resolving needs an approver limit >= this. */
  amount_cents: number | null;
  blocking: boolean;
  payload: Record<string, unknown>;
  state: ApprovalState;
  resolution: string;
  requested_by: number | null;
  resolved_by: number | null;
  resolved_at: string | null;
  reason: string;
  created_at: string;
};

/** One entry of a confirmation.resolve approval's payload.conflicts. */
export type BlockedConflict = {
  reason: string;
  space_id: number;
  hold_id: number | null;
  booking_id: number | null;
};

/** events/serializers.py::ResolveBlockedConfirmationSerializer. */
export type ResolveBlockedInput = {
  resolution: 'retry' | 'cancel_and_refund';
  reason?: string;
};

/** events/serializers.py::VenueEventSettingsSerializer. */
export type EventsVenueSettings = {
  venue: number;
  signature_required: boolean;
  proposal_expiry_days: number;
  hold_length_days: number;
  hold_grace_days: number;
  reply_to_email: string;
  currency: string;
};

export type VenueSettingsInput = Partial<
  Pick<
    EventsVenueSettings,
    | 'proposal_expiry_days'
    | 'hold_length_days'
    | 'hold_grace_days'
    | 'reply_to_email'
  >
>;

export type SpaceInput = Partial<Omit<EventsSpace, 'id' | 'venue'>>;

export type CatalogItemKind =
  | 'FOOD_BEVERAGE'
  | 'MENU'
  | 'ROOM_RENTAL'
  | 'SPACE_RENTAL'
  | 'AV'
  | 'STAFFING'
  | 'BEVERAGE'
  | 'FEE'
  | 'OTHER';

export type CatalogItemInput = Partial<Omit<EventsCatalogItem, 'id' | 'venue'>>;

/** GET/PUT catalog/rules/ (commercial/views.py::CommercialRulesView). */
export type CommercialRules = {
  tax_rules: { category: string; rate_bp: number }[];
  service_fee: { rate_bp: number; taxable: boolean };
  /** Exactly one of percent_bp and flat_cents is set. */
  deposit_rule: {
    percent_bp: number | null;
    flat_cents: number | null;
    due: 'ON_SIGNATURE';
  };
};

/** events/serializers.py::EventSerializer, the extra fields the workspace reads. */
export type EventsEventDetail = EventsEvent & {
  setup_minutes?: number;
  teardown_minutes?: number;
  meal_period?: string | null;
  operational_details?: Record<string, unknown>;
  operations_state?: string;
};

/** events/bookings/services.py::booking_timeline, one audit row. */
export type EventsTimelineEntry = {
  kind: 'audit';
  id: number;
  type: string;
  occurred_at: string;
  actor_type: string;
  actor_id: string;
  payload: Record<string, unknown>;
};

export type CatalogPricingMode = 'PER_PERSON' | 'FLAT' | 'PER_HOUR';

/** commercial/serializers.py::CatalogItemSerializer. */
export type EventsCatalogItem = {
  id: number;
  /** Null for organization-wide items, which a venue can't edit. */
  venue: number | null;
  code: string;
  name: string;
  kind: CatalogItemKind | (string & {});
  pricing_mode: CatalogPricingMode;
  unit_price_cents: number;
  /** Minutes. Hourly items bill in whole increments of this size. */
  billing_increment_minutes: number;
  /** Floor after rounding. 0 means no extra floor. */
  minimum_minutes: number;
  /** Flat items only: the block this one price covers. */
  covered_minutes: number | null;
  tax_category: string;
  service_fee_applies: boolean;
  active: boolean;
};

/** One requested line: POST pricing/quote/, proposal create and update. */
export type ProposalLineInput =
  | { code: string; quantity: number }
  | { code: string; duration_minutes: number; headcount: number };

/** commercial/pricing/engine.py::LineResult. */
export type PricedLine = {
  code: string;
  name: string;
  pricing_mode: CatalogPricingMode;
  unit_price_cents: number;
  /** Guests, 1 for a flat line, or billed increments for an hourly line. */
  quantity: number;
  headcount: number;
  duration_minutes: number | null;
  billing_increment_minutes: number | null;
  covered_minutes: number | null;
  tax_category: string;
  line_total_cents: number;
  service_fee_cents: number;
};

/** commercial/pricing/engine.py::PricingResult.as_dict (also a version's pricing_snapshot). */
export type PricingResult = {
  lines: PricedLine[];
  subtotal_cents: number;
  fees: { kind: string; rate_bp: number; amount_cents: number }[];
  taxes: {
    category: string;
    rate_bp: number;
    taxable_base_cents: number;
    amount_cents: number;
  }[];
  gratuity_cents: number;
  grand_total_cents: number;
  deposit_schedule: { kind: string; due: string; amount_cents: number }[];
};

export type ProposalVersionState =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ACCEPTED'
  | 'SUPERSEDED'
  | 'DECLINED';

/** commercial/serializers.py::ProposalLineSerializer. */
export type ProposalLine = PricedLine & { id: number; position: number };

/** commercial/serializers.py::ProposalVersionSerializer. */
export type ProposalVersion = {
  id: number;
  proposal_id: number;
  booking_id: number;
  number: number;
  state: ProposalVersionState;
  blocks: { kind: string; text?: string }[];
  event_snapshot: Record<string, unknown>;
  /** Empty until the first lines are priced. */
  pricing_snapshot: PricingResult | Record<string, never>;
  terms_text: string;
  consent_text: string;
  published_at: string | null;
  published_by: number | null;
  expires_at: string | null;
  content_hash: string;
  lines: ProposalLine[];
  created_at: string;
  updated_at: string;
};

/** GET bookings/{id}/proposal/ (commercial/views.py::BookingProposalView). */
export type BookingProposalSummary = {
  booking_id: number;
  proposal: {
    id: number;
    current_version_id: number | null;
    draft_version_id: number | null;
    /** A PENDING proposal.publish approval on the draft; it locks the draft. */
    pending_publish_approval_id: number | null;
    /** The event changed since the draft was priced; publish would 409. */
    draft_event_changed: boolean;
    /** Newest first. */
    versions: ProposalVersion[];
  } | null;
};

export type ProposalDraftInput = {
  lines?: ProposalLineInput[];
  intro_text?: string;
};

/** POST proposal-versions/{id}/request-publish/. */
export type PublishResult = ProposalVersion & {
  published: boolean;
  approval_request_id: number | null;
};

/** POST proposal-versions/{id}/preview/. */
export type ProposalPreview = { html: string; content_hash: string };

/** One item of a 409 `conflicts` list (events/views.py exception handler). */
export type EventsConflict = {
  reason: 'CAPACITY' | 'HOLD' | (string & {});
  hold_id: number | null;
  booking_id: number | null;
  space_id: number | null;
  kind: 'SOFT' | 'HARD' | null;
};

export class EventsApiError extends Error {
  readonly status: number | null;
  /** First human-readable message from the error body, when there is one. */
  readonly detail: string | null;
  /** Availability conflicts from a 409; empty otherwise. */
  readonly conflicts: EventsConflict[];

  constructor(
    message: string,
    status: number | null,
    detail?: string | null,
    conflicts: EventsConflict[] = [],
  ) {
    super(message);
    this.name = 'EventsApiError';
    this.status = status;
    this.detail = detail ?? null;
    this.conflicts = conflicts;
  }
}
